import { ReactNode, useMemo, useRef, useState } from 'react';

import cn from 'classnames';

import { useImage } from 'hooks/useImage';

import { Image } from 'ui/atoms/Image';
import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './SliderShowcase.module.scss';

type Props = {
  showArrows?: boolean;
  className?: string;
  children: ReactNode[];
  slides: {
    src: string;
    logos: string[];
  }[];
};

export const SliderShowcase = ({ slides, children, showArrows = true, className }: Props) => {
  const { buildUrl } = useImage();
  const sliderRef = useRef<null | HTMLDivElement>(null);

  const [current, setCurrent] = useState(0);
  const slide = slides[current];

  const buildSrc = useMemo(() => buildUrl({ src: slide.src, width: 760, height: 360 }), [buildUrl, slide.src]);

  const onLeftTransition = () => {
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const onRightTransition = () => {
    setCurrent((prev) => (prev + 1) % slides.length);
  };

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.slide} style={{ backgroundImage: `url(${buildSrc})` }}>
        {showArrows && (
          <button tabIndex={0} aria-label="Arrow left" className={styles.navLeft} onClick={onLeftTransition}>
            <Icon type="arrow-8" />
          </button>
        )}

        <div ref={sliderRef} className={styles.content}>
          {children[current]}
        </div>

        {showArrows && (
          <button tabIndex={0} aria-label="Arrow right" className={styles.navRight} onClick={onRightTransition}>
            <Icon type="arrow-9" />
          </button>
        )}
      </div>

      <div className={styles.indicators}>
        {slides.map((_, idx) => (
          <div
            key={idx}
            className={cn(styles.indicator, { [styles.active]: idx === current })}
            onClick={() => setCurrent(idx)}
          />
        ))}
      </div>

      <div className={styles.logos}>
        {slide.logos.map((logo, idx) => (
          <Image key={idx} width={100} height={40} src={logo} alt="Client logo" />
        ))}
      </div>
    </div>
  );
};
